import React, { useState, useRef, useEffect } from 'react';
import { MoreVertical, Ban, AlertTriangle, Shield, User } from 'lucide-react';
import { useUserManagementStore } from '../../store/userManagementStore';
import ReportUserModal from '../modals/ReportUserModal';
import BlockConfirmationModal from '../modals/BlockConfirmationModal';

interface UserActionsDropdownProps {
  userId: number;
  username: string;
  className?: string;
  onActionComplete?: () => void;
}

const UserActionsDropdown: React.FC<UserActionsDropdownProps> = ({
  userId,
  username,
  className = '',
  onActionComplete
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { getBlockStatus, unblockUser, blockError, clearErrors } = useUserManagementStore();

  // Check initial block status
  useEffect(() => {
    const checkBlockStatus = async () => {
      try {
        const status = await getBlockStatus(userId);
        setIsBlocked(status.currentUserBlocked);
      } catch (error) {
        console.error('Failed to check block status:', error);
      }
    };

    checkBlockStatus();
  }, [userId, getBlockStatus]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleReport = () => {
    setIsOpen(false);
    setShowReportModal(true);
  };

  const handleBlock = () => {
    setIsOpen(false);
    if (isBlocked) {
      handleUnblock();
    } else {
      setShowBlockModal(true);
    }
  };

  const handleUnblock = async () => {
    setIsLoading(true);
    try {
      await unblockUser(userId);
      setIsBlocked(false);
      if (onActionComplete) {
        onActionComplete();
      }
    } catch (error) {
      console.error('Failed to unblock user:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBlockSuccess = () => {
    setIsBlocked(true);
    if (onActionComplete) {
      onActionComplete();
    }
  };

  const handleViewProfile = () => {
    setIsOpen(false);
    // Navigate to user profile - you might want to implement this based on your routing
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 hover:bg-white/[.06] rounded-full transition-colors"
        title="Gebruiker acties"
      >
        <MoreVertical className="w-5 h-5 text-fg-muted" />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-surface rounded-item shadow-panel border border-white/[.09] py-2 z-50">
          {/* View Profile */}
          <button
            onClick={handleViewProfile}
            className="w-full px-4 py-2 text-left text-sm text-fg-card hover:bg-white/[.04] flex items-center gap-3 transition-colors"
          >
            <User className="w-4 h-4 text-neon-cyan" />
            Profiel bekijken
          </button>

          {/* Divider */}
          <div className="border-t border-white/[.08] my-1" />

          {/* Report User */}
          <button
            onClick={handleReport}
            className="w-full px-4 py-2 text-left text-sm text-fg-card hover:bg-white/[.04] flex items-center gap-3 transition-colors"
          >
            <AlertTriangle className="w-4 h-4 text-warning-500" />
            Gebruiker rapporteren
          </button>

          {/* Block/Unblock User */}
          <button
            onClick={handleBlock}
            disabled={isLoading}
            className="w-full px-4 py-2 text-left text-sm hover:bg-white/[.04] flex items-center gap-3 disabled:opacity-50 transition-colors"
          >
            {isBlocked ? (
              <>
                <Shield className="w-4 h-4 text-neon-lime" />
                <span className="text-neon-lime">Deblokkeren</span>
              </>
            ) : (
              <>
                <Ban className="w-4 h-4 text-neon-pink-soft" />
                <span className="text-neon-pink-soft">Gebruiker blokkeren</span>
              </>
            )}
          </button>

          {/* Error message */}
          {blockError && (
            <div className="px-4 py-2 border-t border-white/[.08] mt-1">
              <p className="text-xs text-neon-pink-soft">{blockError}</p>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      <ReportUserModal
        isOpen={showReportModal}
        onClose={() => {
          setShowReportModal(false);
          clearErrors();
        }}
        userId={userId}
        username={username}
      />

      <BlockConfirmationModal
        isOpen={showBlockModal}
        onClose={() => {
          setShowBlockModal(false);
          clearErrors();
        }}
        userId={userId}
        username={username}
        onSuccess={handleBlockSuccess}
      />
    </div>
  );
};

export default UserActionsDropdown;